/**
 * Build-time environment for the FluidDream web client.
 *
 * These are PUBLIC by design — Vite inlines them and the anon key is a browser
 * key. The values must match the other Fluid apps: the same Supabase project and
 * the same cookie domain are what make one sign-in work across the ecosystem.
 */

/** Shared fluidvip Supabase — the SAME project as FluidTalk and FluidGhost. */
export const SUPABASE_URL: string =
  (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim() ?? '';

export const SUPABASE_ANON_KEY: string =
  (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim() ?? '';

/**
 * Scopes the session cookie to the parent domain ('.fluidvip.com') so a user
 * already signed in on talk. or ghost. lands straight into dream. without a
 * second login. Empty in local dev, where there's no shared parent.
 */
export const COOKIE_DOMAIN: string =
  (import.meta.env.VITE_COOKIE_DOMAIN as string | undefined)?.trim() ?? '';

/** True when real auth is wired. Both halves or nothing. */
export const AUTH_CONFIGURED: boolean =
  SUPABASE_URL.length > 0 && SUPABASE_ANON_KEY.length > 0;

/**
 * Cloudflare Turnstile site key (PUBLIC). The shared fluidvip GoTrue has
 * Turnstile enforced on password sign-in, so a login without a token is
 * rejected — when this is set we render the widget and require one.
 */
export const TURNSTILE_SITE_KEY: string =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim() ?? '';

/** Where the API lives. Same origin in production; nginx proxies /api. */
export const API_BASE: string = (() => {
  const raw = (import.meta.env.VITE_API_BASE as string | undefined)?.trim();
  const base = (raw && raw.length > 0 ? raw : '/api').replace(/\/+$/, '');
  return base.endsWith('/api') ? base : `${base}/api`;
})();

/** Where account creation lives. Accounts are shared, so sign-up is central —
 *  on the hub, not on a sibling product that happened to build it first. */
export const SIGNUP_URL = 'https://fluidvip.com/register/';

/**
 * FluidCloud's web origin — where picker.js is served from, for importing an
 * image you already have in your Drive. Must match the FluidCloud stack this
 * deployment writes to, and THIS app's origin has to be in that stack's
 * first-party picker allowlist or the picker refuses to open.
 */
export const FLUIDCLOUD_WEB_URL: string = (
  (import.meta.env.VITE_FLUIDCLOUD_WEB_URL as string | undefined)?.trim() || 'https://cloud.fluidvip.com'
).replace(/\/+$/, '');
